import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEye, FaTimes, FaUndo, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "../../../redux/features/modalSlice";

import Pagination from "../../../common/Pagination";
import { formatDateTime } from "../../../Utils/formatters";
import {
  getDeletedEnquriesList,
  RestoreEnquiry,
  PermanentDeleteEnquiry,
} from "../../../redux/services/EnquriesService/enquries.Services";
import { successToast, errorToast } from "../../../Utils/toast";

function DeletedEnquiryList() {
  const dispatch = useDispatch();
  const { refreshData } = useSelector((state) => state.modal);

  const [showRemarkPopup, setShowRemarkPopup] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState("");

  const [rolesData, setRolesData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);

  const [totalRoles, setTotalRoles] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const fetchDeletedEnquiry = async (pageNo = 1, isInitialLoad = false) => {
    try {
      if (isInitialLoad) setInitialLoading(true);
      else setTableLoading(true);

      const payload = { page: pageNo, per_page: limit };
      const res = await getDeletedEnquriesList(payload);

      setRolesData(res?.data || []);
      setPage(res?.current_page || 1);
      setTotalPages(res?.last_page || 1);
      setTotalRoles(res?.total || 0);
      setSelectedIds([]);
    } catch (err) {
      console.log(err);
    } finally {
      setInitialLoading(false);
      setTableLoading(false);
    }
  };

  const handleRemarkClick = (remark) => {
    setSelectedRemark(remark || "No Remark");
    setShowRemarkPopup(true);
  };

  const handleRestore = async (item) => {
    try {
      setTableLoading(true);
      const res = await RestoreEnquiry({ items: [{ id: item.id, type: item.type }] });

      if (res?.success) {
        successToast(res?.message || "Enquiry restored successfully");
        fetchDeletedEnquiry(page, false);
      } else {
        errorToast(res?.message || "Failed to restore enquiry");
      }
    } catch (err) {
      console.log(err);
      errorToast(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setTableLoading(false);
    }
  };

  // 🆕 Wrapper for Single Permanent Delete
  const executeSinglePermanentDelete = async (id, type) => {
    // API ko { items: [{ id, type }] } format me bhej rahe hain
    return await PermanentDeleteEnquiry({ items: [{ id, type }] });
  };

  // 🆕 Wrapper for Bulk Permanent Delete
  const executeBulkPermanentDelete = async (itemsArray) => {
    // itemsArray pehle se hi [{ id, type }] format me hai
    return await PermanentDeleteEnquiry({ items: itemsArray });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(rolesData.map((item) => item.id));
    else setSelectedIds([]);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkRestore = async () => {
    if (selectedIds.length === 0) return;
    try {
      setIsBulkProcessing(true);
      const selectedItems = rolesData.filter((item) => selectedIds.includes(item.id));
      const res = await RestoreEnquiry({ items: selectedItems.map((i) => ({ id: i.id, type: i.type })) });

      if (res?.success) {
        successToast(`${selectedIds.length} enquiries restored successfully`);
        setSelectedIds([]);
        fetchDeletedEnquiry(page, false);
      } else {
        errorToast(res?.message || "Failed to restore selected enquiries");
      }
    } catch (err) {
      console.log(err);
      errorToast(err?.response?.data?.message || "Failed to restore selected enquiries");
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !tableLoading) {
      setPage(newPage);
      fetchDeletedEnquiry(newPage, false);
    }
  };

  useEffect(() => {
    fetchDeletedEnquiry(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData, limit]);

  if (initialLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  const isAllSelected = rolesData.length > 0 && selectedIds.length === rolesData.length;

  return (
    <div>
      {/* HEADER */}
      <div className="d-flex align-items-center justify-content-between page-header-breadcrumb">
        <div className="title_member">
          <h4>Deleted</h4>
          <nav className="page-breadcrumb">
            <Link to="/">Home</Link>
            <span className="mx-2">/</span>
            <span>Deleted</span>
          </nav>
        </div>

        {/* BULK ACTIONS */}
        {selectedIds.length > 0 && (
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleBulkRestore}
              disabled={isBulkProcessing || tableLoading}
            >
              {isBulkProcessing ? <span className="spinner-border spinner-border-sm"></span> : <FaUndo />}
              Restore ({selectedIds.length})
            </button>

            <button
              className="btn btn-danger d-flex align-items-center gap-2"
              onClick={() => {
                const selectedItems = rolesData
                  .filter((item) => selectedIds.includes(item.id))
                  .map((i) => ({ id: i.id, type: i.type }));

                dispatch(
                  openModal({
                    type: "delete-enquiry",
                    title: "Permanently Delete Enquiries",
                    data: {
                      name: `${selectedIds.length} selected enquiries`,
                      // 👇 Wrapper function pass kiya hai
                      deleteFunction: () => executeBulkPermanentDelete(selectedItems),
                      refreshData: () => {
                        setSelectedIds([]);
                        fetchDeletedEnquiry(page, false);
                      },
                    },
                  })
                );
              }}
              disabled={isBulkProcessing || tableLoading}
            >
              <FaTrash /> Delete Permanently ({selectedIds.length})
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="main_page">
        <div className="st-wrapper-card text-center">
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      disabled={rolesData.length === 0 || tableLoading || isBulkProcessing}
                    />
                  </th>
                  <th>ID</th>
                  <th>Date & Time</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Enquiry From</th>
                  {/* <th>Remark</th> */}
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <div className="spinner-border text-primary"></div>
                    </td>
                  </tr>
                ) : rolesData.length > 0 ? (
                  rolesData.map((item, index) => (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => handleSelectOne(item.id)}
                          disabled={isBulkProcessing}
                        />
                      </td>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                        {formatDateTime(item.created_at)}
                      </td>
                      <td>{item.name}</td>
                      <td>{item.mobile || item.phone}</td>
                      <td>{item.email}</td>
                      <td>{item.type}</td>
                      {/* <td>
                        <div
                          className="remark-cell text-primary"
                          style={{ cursor: "pointer", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          onClick={() => handleRemarkClick(item.remarks)}
                        >
                          {item.remarks || "-"}
                        </div>
                      </td> */}

                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <button
                            className="st-action-btn st-delete-btn"
                            onClick={() => dispatch(openModal({ type: "view-enquiry", title: "View Enquiry", data: item }))}
                            disabled={isBulkProcessing}
                          >
                            <FaEye />
                          </button>

                          <button
                            className="st-action-btn st-edit-btn"
                            title="Restore Enquiry"
                            onClick={() => handleRestore(item)}
                            disabled={tableLoading || isBulkProcessing}
                          >
                            <FaUndo />
                          </button>

                          {/* 🆕 SINGLE PERMANENT DELETE (FIXED PAYLOAD) */}
                          <button
                            className="st-action-btn st-delete-btn text-danger"
                            title="Permanently Delete"
                            onClick={() =>
                              dispatch(
                                openModal({
                                  type: "delete-enquiry",
                                  title: "Permanently Delete Enquiry",
                                  data: {
                                    // Popup me warning ke liye name change kiya
                                    name: `${item.name} (Permanent Delete)`,
                                    // 👇 Wrapper function pass kiya hai
                                    deleteFunction: () => executeSinglePermanentDelete(item.id, item.type),
                                    refreshData: () => fetchDeletedEnquiry(page, false),
                                  },
                                })
                              )
                            }
                            disabled={tableLoading || isBulkProcessing}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-5">
                      <h5 className="mb-0">No Deleted Enquiries Found</h5>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Remark Modal */}
        {showRemarkPopup && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Remark Details</h5>
                  <button type="button" className="btn-close text-light" style={{ background: "transparent" }} onClick={() => setShowRemarkPopup(false)}>
                    <FaTimes />
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: "pre-wrap" }}>{selectedRemark}</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowRemarkPopup(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeletedEnquiryList;